import { SEARCH_DEBOUNCE_MS } from '@/services/photovogue/defaults';
import { create } from 'zustand';

import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { createInitialState, InitialState } from '@repo/ui/state';
import { getPhotoVogueService } from '@/services/photovogue';
import type {
  Pagination,
  Photographer,
  PhotographerFilters,
  PhotographerQueryParams,
  PhotographerSort,
} from '@/services/photovogue';

const DEFAULT_PAGINATION: Pagination = { page: 0, limit: 24, skip: 0 };
const DEFAULT_SORT: PhotographerSort = { field: 'name', direction: 'asc' };

const defaultParams = (): PhotographerQueryParams => ({
  filters: {},
  pagination: { ...DEFAULT_PAGINATION },
  sort: { ...DEFAULT_SORT },
});

const withSkip = (pagination: Pagination): Pagination => ({
  ...pagination,
  skip: pagination.page * pagination.limit,
});

export interface PhotographersStore extends InitialState<Photographer[]> {
  _ids: string[];
  count: number;
  params: PhotographerQueryParams;
  loadingSpinner: boolean;
  updatingIds: string[];

  fetchPhotographers: (
    params: PhotographerQueryParams,
    silent?: boolean,
    debounceSearch?: boolean,
  ) => Promise<void>;
  init: () => Promise<void>;
  updateFilters: (
    filters:
      | PhotographerFilters
      | ((filters: PhotographerFilters) => PhotographerFilters),
    debounceSearch?: boolean,
  ) => Promise<void>;
  updatePagination: (
    pagination: Pagination | ((pagination: Pagination) => Pagination),
  ) => Promise<void>;
  updateSort: (
    sort: PhotographerSort | ((sort: PhotographerSort) => PhotographerSort),
  ) => Promise<void>;
  hasActiveFilters: () => boolean;
  resetFilters: () => Promise<void>;
  moderatePhotographer: (
    id: string,
    state: Photographer['state'],
  ) => Promise<void>;
  updateSpotlight: (id: string, spotlight: boolean) => Promise<void>;
  deletePhotographer: (id: string) => Promise<boolean>;
  reset: () => void;
}

export const initialState = {
  _ids: [],
  count: 0,
  loadingSpinner: true,
  updatingIds: [],
  params: defaultParams(),
  ...createInitialState<Photographer[]>([]),
};

// Only the latest list request may publish results, errors, or loading state.
let requestVersion = 0;
let resetVersion = 0;

export const store = create<PhotographersStore>((set, store) => ({
  ...initialState,

  fetchPhotographers: async (
    params,
    silent = false,
    debounceSearch = false,
  ) => {
    const version = ++requestVersion;
    set((state) => ({
      ...state,
      loading: !silent,
      loadingSpinner: true,
      data: silent ? state.data : [],
    }));
    try {
      if (debounceSearch) {
        await new Promise((resolve) => setTimeout(resolve, SEARCH_DEBOUNCE_MS));
        if (version !== requestVersion) return;
      }
      const response = await getPhotoVogueService().getPhotographers(params);
      if (version !== requestVersion) return;
      set((state) => ({
        ...state,
        error: null,
        _ids: response._ids,
        data: response.data,
        count: response.count,
      }));
    } catch (error) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to load photographers' });
      set((state) => ({
        ...state,
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load photographers',
      }));
    } finally {
      if (version === requestVersion)
        set((state) => ({ ...state, loading: false, loadingSpinner: false }));
    }
  },

  init: async () => {
    await store().fetchPhotographers(store().params);
  },

  updateFilters: async (filtersUpdater, debounceSearch = false) => {
    const current = store().params.filters;
    const filters =
      typeof filtersUpdater === 'function'
        ? filtersUpdater(current)
        : filtersUpdater;
    const params: PhotographerQueryParams = {
      ...store().params,
      filters,
      pagination: withSkip({ ...store().params.pagination, page: 0 }),
    };
    set((state) => ({ ...state, params }));
    await store().fetchPhotographers(params, true, debounceSearch);
  },

  updatePagination: async (paginationUpdater) => {
    const current = store().params.pagination;
    const next =
      typeof paginationUpdater === 'function'
        ? paginationUpdater(current)
        : paginationUpdater;
    const params: PhotographerQueryParams = {
      ...store().params,
      pagination: withSkip(next),
    };
    set((state) => ({ ...state, params }));
    await store().fetchPhotographers(params);
  },

  updateSort: async (sortUpdater) => {
    const current = store().params.sort;
    const sort =
      typeof sortUpdater === 'function' ? sortUpdater(current) : sortUpdater;
    const params: PhotographerQueryParams = { ...store().params, sort };
    set((state) => ({ ...state, params }));
    await store().fetchPhotographers(params, true);
  },

  hasActiveFilters: () => {
    const { filters } = store().params;
    return Boolean(
      filters.states?.length ||
        filters.tier ||
        filters.country ||
        filters.search?.trim() ||
        filters.withPhotosOnly,
    );
  },

  resetFilters: async () => {
    await store().updateFilters({});
  },

  moderatePhotographer: async (id, photographerState) => {
    set((state) => ({
      ...state,
      updatingIds: [...state.updatingIds, id],
    }));
    try {
      const updated = await getPhotoVogueService().moderatePhotographer(
        id,
        photographerState,
      );
      set((state) => ({
        ...state,
        data: state.data.map((item) => (item.id === id ? updated : item)),
        error: null,
      }));
    } catch (error) {
      showErrorToast({ title: 'Unable to update photographer' });
      set((state) => ({ ...state, error: (error as Error).message }));
    } finally {
      set((state) => ({
        ...state,
        updatingIds: state.updatingIds.filter((item) => item !== id),
      }));
    }
  },

  updateSpotlight: async (id, spotlight) => {
    set((state) => ({
      ...state,
      updatingIds: [...state.updatingIds, id],
    }));
    try {
      const updated = await getPhotoVogueService().updatePhotographerSpotlight(
        id,
        spotlight,
      );
      set((state) => ({
        ...state,
        data: state.data.map((item) => (item.id === id ? updated : item)),
        error: null,
      }));
    } catch (error) {
      showErrorToast({ title: 'Unable to update photographer spotlight' });
      set((state) => ({ ...state, error: (error as Error).message }));
    } finally {
      set((state) => ({
        ...state,
        updatingIds: state.updatingIds.filter((item) => item !== id),
      }));
    }
  },

  deletePhotographer: async (id) => {
    if (store().updatingIds.includes(id)) return false;
    const version = resetVersion;
    set((state) => ({ updatingIds: [...state.updatingIds, id] }));
    try {
      await getPhotoVogueService().deletePhotographer(id);
      if (version !== resetVersion) return true;
      // Fetch canonical rows/counts; invalidate reads started before deletion.
      const params = store().params;
      await store().fetchPhotographers(params);
      const current = store();
      if (
        current.params === params &&
        !current.error &&
        params.pagination.page > 0 &&
        !current.data.length
      ) {
        await current.updatePagination({
          ...params.pagination,
          page: Math.max(
            0,
            Math.ceil(current.count / params.pagination.limit) - 1,
          ),
        });
      }
      return true;
    } catch {
      return false;
    } finally {
      if (version === resetVersion) {
        set((state) => ({
          updatingIds: state.updatingIds.filter((item) => item !== id),
        }));
      }
    }
  },

  reset: () => {
    ++requestVersion;
    ++resetVersion;
    set((state) => ({
      ...state,
      ...initialState,
      params: defaultParams(),
    }));
  },
}));
